import { AnyAction, Dispatch } from 'redux';
import api from '../services/api';
import { normalize } from 'normalizr';
import { tenants } from './schema';
import { Cycle, Tenant } from '../types';
import { logout } from './auth';
import { UPLOAD_TO_PMS_ACTIONS } from '.';

export const handleError = (
  res: any,
  dispatch: Dispatch,
  msg?: { message: string; type: string }
) => {
  if (res.response.status === 401) {
    logout();
  } else if (res.response.status === 403) {
    if (res.response.data?.error.includes('Un-authorized request-00')) {
      dispatch({
        type: 'SHOW_NOTIFICATIONS',
        payload: {
          message: `User does not have permission to perform this action`,
          type: 'warning',
        },
      });
    }
  } else {
    if (msg || res.response.data?.error) {
      dispatch({
        type: 'SHOW_NOTIFICATIONS',
        payload: {
          message: msg?.message || res.response.data?.error,
          type: msg?.type || 'error',
        },
      });
    } else {
      dispatch({
        type: 'SHOW_NOTIFICATIONS',
        payload: {
          message: 'Something went wrong!',
          type: 'warning',
        },
      });
    }
  }
  console.log(res);
};

export const fetchStrategy = (propertyCode: string) => (dispatch: Dispatch) => {
  let parameters = {};
  if (propertyCode.length > 0) {
    parameters = {
      propertyCode: propertyCode,
    };
  }

  api
    .getSettings(parameters)
    .then((res) => {
      let strategy = undefined;
      if (res.data) {
        res.data.forEach((item: any) => {
          if (item.Key === 'Renewal.Strategy') {
            strategy = item.Value;
          }
        });
      }

      dispatch({
        type: 'FETCH_STRATEGY',
        payload: strategy,
        meta: {
          track: true,
        },
      });
    })
    .catch((err) => {
      handleError(err, dispatch);
      console.log(err);
    });
};

export const getDashboardData =
  (offerCycle: string) => (dispatch: Dispatch) => {
    dispatch({
      type: 'IS_LOADING',
      payload: true,
    });
    dispatch({
      type: UPLOAD_TO_PMS_ACTIONS.IS_LOADING,
      payload: {
        isLoading: true,
      },
    });
    api
      .getDashboardData(offerCycle)
      .then((res: any) => {
        let data = normalize(res.data.dashboardData, [tenants]);
        dispatch({
          type: 'DASHBOARD_DATA',
          payload: {
            original: res.data.dashboardData,
            normalized: data,
            strategy: res.data.strategy,
          },
          meta: {
            track: true,
          },
        });
        dispatch({
          type: 'IS_LOADING',
          payload: false,
        });
        dispatch({
          type: UPLOAD_TO_PMS_ACTIONS.IS_LOADING,
          payload: {
            isLoading: false,
          },
        });
        dispatch({
          type: UPLOAD_TO_PMS_ACTIONS.SET_REFRESH_FUNC_PARAM,
          payload: {
            refreshFuncParam: offerCycle,
          },
        });
        dispatch({
          type: UPLOAD_TO_PMS_ACTIONS.FETCH,
          payload: {
            data: Object.values(data.entities.tenants ?? {}).map((tenant) => ({
              propertyCode: tenant.Property_Code,
              tenantCode: tenant.Tenant_Code,
              status: tenant.Uploaded,
              isApproved: tenant.IsApproved,
            })),
          },
        });
      })
      .catch((err) => {
        handleError(err, dispatch);
        dispatch({
          type: 'DASHBOARD_DATA_FAILED',
          payload: {},
          meta: {
            track: true,
          },
        });
        console.log(err);
      })
      .finally(() => {
        dispatch({
          type: 'IS_LOADING',
          payload: false,
        });
      });
  };

export const searchThroughAllCycles =
  (offerCycles: Array<Cycle> | undefined, findPhrase: any) =>
  (dispatch: Dispatch) => {
    let preparedOfferCycles: any = [];

    if (offerCycles) {
      offerCycles.forEach((item: any, i: number) => {
        preparedOfferCycles.push(api.getDashboardData(item.OfferCycle));
      });
    }

    Promise.all(preparedOfferCycles)
      .then((lists) => {
        let results: any = [];

        lists.forEach((list: any, i: number) => {
          let listResults: any = [];
          list.data.dashboardData.forEach((item: any, y: number) => {
            String(Object.values(item));
            if (String(Object.values(item)).includes(findPhrase.name)) {
              listResults.push(item);
            }
          });
          results.push({ cycle: offerCycles[i].OfferCycle, data: listResults });
        });

        dispatch({
          type: 'SEARCH_RESULTS',
          payload: results,
          meta: {
            track: true,
          },
        });
      })
      .catch((err) => {
        handleError(err, dispatch);
      });
  };

export const getKPIData = () => (dispatch: Dispatch) => {
  if (process.env.REACT_APP_TYPE === 'dev') {
    dispatch({
      type: 'IS_LOADING',
      payload: true,
    });
  }
  api
    .getKPIData()
    .then((res: any) => {
      dispatch({
        type: 'KPI_DATA',
        payload: res.data,
        meta: {
          track: true,
        },
      });
    })
    .catch((err) => {
      handleError(err, dispatch);
      console.log(err);
    })
    .finally(() => {
      if (process.env.REACT_APP_TYPE === 'dev') {
        dispatch({
          type: 'IS_LOADING',
          payload: false,
        });
      }
    });
};
export const getRenewalOffers = (tenantId: number) => (dispatch: Dispatch) => {
  api
    .getRenewalOffers(tenantId)
    .then((res: any) => {
      dispatch({
        type: 'RENEWAL_OFFERS',
        payload: res.data,
        meta: {
          track: true,
        },
      });
    })
    .catch((err) => {
      handleError(err, dispatch);
      console.log(err);
    });
};

export const setSelectedTenant = (value: any) => (dispatch: Dispatch) =>
  dispatch({
    type: 'SELECTED_TENANT',
    payload: value,
    meta: {
      track: true,
      includeData: true,
    },
  });
export const setMonthPart = (value: any) => (dispatch: Dispatch) =>
  dispatch({
    type: 'SELECTED_PART',
    payload: value,
    meta: {
      track: true,
      includeData: true,
    },
  });
export const setSearchQuery = (value: any) => (dispatch: Dispatch) => {
  return api
    .searchTenant(value)
    .then((res: any) => {
      if (res.data.result) {
        dispatch({
          type: 'SEARCH_RESULT',
          payload: res.data.result,
          meta: {
            track: true,
            includeData: true,
          },
        });
      } else {
        dispatch({
          type: 'SEARCH_OPTIONS',
          payload: res.data.options,
          meta: {
            track: true,
            includeData: true,
          },
        });
      }
    })
    .catch((err) => {
      handleError(err, dispatch);
      console.log(err);
    });
};

export const getSelectedResult = (value: any) => (dispatch: Dispatch) => {
  // dispatch({
  //     type:'IS+LOADING'
  // })
  return api
    .searchTenant(value)
    .then((res: any) => {
      dispatch({
        type: 'SEARCH_RESULT',
        payload: res.data.result,
        meta: {
          track: true,
          includeData: true,
        },
      });
    })
    .catch((err: any) => {
      handleError(err, dispatch);
      console.log(err);
    });
};

export const resetSelectedResult = () => (dispatch: Dispatch) =>
  dispatch({
    type: 'SEARCH_RESULT',
    payload: undefined,
    meta: {
      track: true,
    },
  });

export const setIsLoading = (value: any) => (dispatch: Dispatch) =>
  dispatch({
    type: 'IS_LOADING',
    payload: value,
  });

// export const setSelectedMonth = (month:) => dispatch => {
//     dispatch({
//         type: 'SELECTED_MONTH',
//         payload: month,
//         meta: {
//             track: true,
//             includeData: true
//         }
//     })

// }

type AddOfferData = {
  tenant_code: string;
  rent: number;
  end_date: string; //Lease end date
  property_code: string;
  term: number;
  offer_cycle: string;
};

export const addOffer =
  (addOfferData: AddOfferData) => (dispatch: Dispatch) => {
    return api
      .addOffer(addOfferData)
      .then((res: any) => {
        dispatch({
          type: 'ADD_OFFER',
          payload: res.data.offer,
          meta: {
            track: true,
            includeData: true,
          },
        });
        dispatch({
          type: 'SHOW_NOTIFICATIONS',
          payload: { message: `Offer Added`, type: 'success' },
        });
      })
      .catch((err) => {
        handleError(err, dispatch);
        console.log(err);
      });
  };

export const approveOffer =
  (tenant_code: string) => (dispatch: Dispatch, getState: any) => {
    let storeState = getState();
    let { selectedOfferCycle } = storeState.offers;

    dispatch({
      type: 'IS_LOADING',
      payload: true,
    });
    api
      .approveOffer({
        tenant_code,
        offer_cycle: selectedOfferCycle.OfferCycle,
      })
      .then(() => {
        dispatch({
          type: 'HANDLE_APPROVE',
          payload: tenant_code,
          meta: {
            track: true,
            includeData: true,
          },
        });
        dispatch({
          type: 'SHOW_NOTIFICATIONS',
          payload: {
            message: ` Offers approved  for Tenant ${tenant_code}`,
            type: 'success',
          },
        });
      })
      .catch((err) => {
        handleError(err, dispatch);
        console.log(err);
      })
      .finally(() => {
        dispatch({
          type: 'IS_LOADING',
          payload: false,
        });
      });
  };
export const disApproveOffer =
  (tenant_code: string) => (dispatch: Dispatch, getState: any) => {
    let storeState = getState();
    let { selectedOfferCycle } = storeState.offers;

    dispatch({
      type: 'IS_LOADING',
      payload: true,
    });
    api
      .disApproveOffer({
        tenant_code,
        offer_cycle: selectedOfferCycle.OfferCycle,
      })
      .then((res) => {
        dispatch({
          type: 'HANDLE_DISAPPROVE',
          payload: tenant_code,
          meta: {
            track: true,
            includeData: true,
          },
        });
        dispatch({
          type: 'SHOW_NOTIFICATIONS',
          payload: {
            message: ` Approval removed for Tenant ${tenant_code}`,
            type: 'info',
          },
        });
      })
      .catch((err) => {
        handleError(err, dispatch);
        console.log(err);
      })
      .finally(() => {
        dispatch({
          type: 'IS_LOADING',
          payload: false,
        });
      });
  };

export const setApproveAll =
  (tcodes: any, checked: any, offer_cycle?: any) => (dispatch: Dispatch) => {
    dispatch({
      type: 'IS_LOADING',
      payload: true,
    });
    api
      .toggleApproveAll({ tcodes, checked, offer_cycle })
      .then(() => {
        dispatch({
          type: 'APPROVE_ALL',
          payload: { tcodes, checked },
          meta: {
            track: true,
            includeData: true,
          },
        });
        dispatch({
          type: 'SHOW_NOTIFICATIONS',
          payload: {
            message: `Offers ${checked ? 'approved' : 'approval removed'}`,
            type: 'success',
          },
        });
        // set notification with length
        // dispatch({
        //     type:'NOTIFICATION',
        //     payload
        // })
      })
      .catch((err) => {
        console.log(err);
        handleError(err, dispatch);
      })
      .finally(() => {
        dispatch({
          type: 'IS_LOADING',
          payload: false,
        });
      });
  };

type UpdateOfferData = {
  tenant_code: string;
  offerid: string;
  new_rent: number;
  old_rent: number;
};
export const updateOffer = (data: UpdateOfferData) => (dispatch: Dispatch) => {
  console.log(data);
  return api
    .updateOffer(data)
    .then(() => {
      dispatch({
        type: 'UPDATE_OFFER',
        payload: data,
        meta: {
          track: true,
          includeData: true,
        },
      });

      dispatch({
        type: 'SHOW_NOTIFICATIONS',
        payload: { message: 'Offer Updated', type: 'success' },
      });
      //dispatch notification of update
    })
    .catch((err) => {
      handleError(err, dispatch);
      console.log(err.response);
    });
};

export const setSelectedRange = (range?: string) => (dispatch: Dispatch) =>
  dispatch({
    type: 'SELECTED_RANGE',
    payload: range,
    meta: {
      track: true,
      includeData: true,
    },
  });

export const toggleExcludeOffers =
  (tenant_code: string, exclude: boolean) =>
  (dispatch: Dispatch, getState: any) => {
    dispatch({
      type: 'IS_LOADING',
      payload: true,
    });
    let storeState = getState();
    let { selectedOfferCycle, dashboardData } = storeState.offers;
    const selectedStrategy = dashboardData.find(
      (elem: Tenant) => elem.Tenant_Code === tenant_code
    )?.SelectedStrategy;
    //TODO fix object notation and key structure
    let data = {
      tenant_code,
      offer_cycle: selectedOfferCycle.OfferCycle,
      exclude,
      strategy: selectedStrategy,
    };
    api
      .toggleExcludeOffers(data)
      .then(() => {
        dispatch({
          type: 'TOGGLE_EXCLUDE',
          payload: data,
          meta: {
            track: true,
            includeData: true,
          },
        });
        dispatch({
          type: 'SHOW_NOTIFICATIONS',
          payload: {
            message: ` Exclude ${
              exclude ? 'added' : 'removed'
            } for Tenant ${tenant_code}`,
            type: exclude ? 'success' : 'info',
          },
        });
      })
      .catch((err) => {
        console.log(err);
        handleError(err, dispatch);
      })
      .finally(() => {
        dispatch({
          type: 'IS_LOADING',
          payload: false,
        });
      });
  };

export const setSelectedOfferCycle =
  (offerCycle: any) => (dispatch: Dispatch) => {
    dispatch({
      type: 'UPDATE_OFFER_CYCLE',
      payload: offerCycle,
      meta: {
        track: true,
      },
    });
  };

export const deleteOffer =
  (offer_id: any, Tenant_Code: any) => (dispatch: Dispatch) => {
    dispatch({
      type: 'IS_LOADING',
      payload: true,
    });
    api
      .removeOffer(offer_id)
      .then(() => {
        dispatch({
          type: 'REMOVE_OFFER',
          payload: { offer_id, Tenant_Code },
          meta: {
            track: true,
            includeData: true,
          },
        });
        dispatch({
          type: 'SHOW_NOTIFICATIONS',
          payload: { message: 'Offer deleted', type: 'success' },
        });
      })
      .catch((err) => {
        handleError(err, dispatch);
      })
      .finally(() => {
        dispatch({
          type: 'IS_LOADING',
          payload: false,
        });
      });
  };

export const getCycles = (type?: string) => (dispatch: Dispatch) => {
  dispatch({
    type: 'IS_LOADING',
    payload: true,
  });
  api
    .getOfferCycles(type)
    .then((res: any) => {
      dispatch({
        type: 'REPORTS_DATA',
        payload: res.data,
        meta: {
          track: true,
        },
      });

      dispatch({
        type: 'IS_LOADING',
        payload: false,
      });
    })

    .catch((err) => {
      handleError(err, dispatch);
      dispatch({
        type: 'IS_LOADING',
        payload: false,
      });
    })
    .finally(() => {});
};

export const setSelectedOfferState =
  (offerState?: string) => (dispatch: Dispatch) => {
    dispatch({
      type: 'SET_OFFER_STATE',
      payload: offerState,
      meta: {
        track: true,
      },
    });
  };

export const setView = (view: string) => (dispatch: Dispatch) => {
  dispatch({
    type: 'SET_SELECTED_VIEW',
    payload: view,
    meta: {
      track: true,
    },
  });
};

export const setShowAddOfferDialog =
  (show: boolean, selectedTenant?: string) => (dispatch: Dispatch) => {
    dispatch({
      type: 'SET_SHOW_ADD_OFFER_DIALOG',
      payload: { show, selectedTenant },
      meta: {
        track: true,
      },
    });
  };

export const setShowEditOfferDialog =
  (show: boolean, offerId?: string, selectedTenant?: string) =>
  (dispatch: Dispatch) => {
    dispatch({
      type: 'SET_SHOW_EDIT_OFFER_DIALOG',
      payload: { show, selectedTenant, offerId },
      meta: {
        track: true,
      },
    });
  };

export const setShowFilterSettingsDrawer =
  (show: boolean) => (dispatch: Dispatch) =>
    dispatch({
      type: 'SET_SHOW_FILTER_SETTINGS',
      payload: show,
      meta: {
        track: true,
      },
    });

export const setStickinessFilter = (score?: number) => (dispatch: Dispatch) => {
  dispatch({
    type: 'SET_STICKINESS_FILTER',
    payload: score,
    meta: {
      track: true,
    },
  });
};

export const resetRenewalSummaryLoadingStatus = () => (dispatch: Dispatch) => {
  dispatch({
    type: 'RENEWAL_SUMMARY_IS_LOADING',
    payload: false,
    meta: {
      track: true,
    },
  });
};

export const getRenewalSummary =
  (
    offer_cycle: string,
    property_code?: string,
    bedRoom?: string | number,
    market?: string
  ) =>
  (dispatch: Dispatch) => {
    dispatch({
      type: 'IS_LOADING',
      payload: true,
    });

    api
      .getRenewalSummary({
        offer_cycle,
        property_code,
        bedRoom,
        market,
      })
      .then((res) => {
        dispatch({
          type: 'GET_RENEWAL_SUMMARY',
          payload: res.data,
          meta: {
            track: true,
          },
        });
        dispatch({
          type: 'IS_LOADING',
          payload: false,
        });
      })
      .catch((err) => {
        console.error(err);
        handleError(err, dispatch);
      })
      .finally(() => {
        dispatch({
          type: 'IS_LOADING',
          payload: false,
        });
      });
  };

export const shiftOffers =
  (shiftedOffers: UpdateOfferData[]) => (dispatch: Dispatch) => {
    return api
      .shiftOffers(shiftedOffers)
      .then((response) => {
        const { updatedOffers } = response.data;
        if (updatedOffers && Array.isArray(updatedOffers)) {
          updatedOffers.forEach((offer) => {
            dispatch({
              type: 'UPDATE_OFFER',
              payload: {
                offerid: offer.id,
                new_rent: offer.Rent,
              },
              meta: {
                track: true,
                includeData: true,
              },
            });
          });
          dispatch({
            type: 'SHOW_NOTIFICATIONS',
            payload: { message: 'Offers Updated', type: 'success' },
          });
        }
      })
      .catch((err) => {
        handleError(err, dispatch);
      });
  };
export const bulkApproveOffers = (data: any) => (dispatch: Dispatch) => {
  dispatch({
    type: 'IS_LOADING',
    payload: true,
  });
  api
    .bulkApproveRenewals(data)
    .then(() => {
      dispatch({
        type: 'BULK_APPROVE_OFFERS',
        payload: data,
        meta: {
          track: true,
          includeData: true,
        },
      });
      dispatch({
        type: 'IS_LOADING',
        payload: false,
      });
      dispatch({
        type: 'SHOW_NOTIFICATIONS',
        payload: {
          message: data.approve ? ` Offers approved` : `Offers Un-Approved`,
          type: 'success',
        },
      });
      return dispatch(
        getDashboardData(data.offerCycle) as unknown as AnyAction
      );
    })
    .catch((err) => {
      handleError(err, dispatch);
      console.log(err);
      dispatch({
        type: 'IS_LOADING',
        payload: false,
      });
    });
};

// export const openUploadToPMSModal = () => (dispatch: Dispatch) => {
//   dispatch({
//     type: 'OPEN_UPLOAD_TO_PMS_MODAL',
//     payload: {},
//   });
// };

// export const closeUploadToPMSModal = () => (dispatch: Dispatch) => {
//   dispatch({
//     type: 'CLOSE_UPLOAD_TO_PMS_MODAL',
//     payload: {},
//   });
// };

export const setUploadToPMSIsLoading =
  (isLoading: boolean) => (dispatch: Dispatch) => {
    dispatch({
      type: 'CLOSE_UPLOAD_TO_PMS_MODAL',
      payload: { isLoading: isLoading },
    });
  };

export const syncOffersToUpload = () => (dispatch: Dispatch) => {
  dispatch({
    type: 'SYNC_OFFERS_TO_UPLOAD',
    payload: {},
  });
};

export const getOffersToUpload = () => (dispatch: Dispatch) => {
  dispatch({
    type: 'GET_OFFERS_TO_UPLOAD',
    payload: {},
  });
};
